import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Mail, BookOpen, ArrowRight, Loader2, Tag, User, Receipt } from 'lucide-react';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      fetchOrderDetails();
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const fetchOrderDetails = async () => {
    try {
      const res = await fetch(`${API}/payments/order-details?session_id=${sessionId}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setOrderDetails(data);
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTrackFullName = (track) => {
    const names = {
      'PMP': 'Project Management Professional (PMP)',
      'CAPM': 'Certified Associate in Project Management (CAPM)',
      'PSM': 'Professional Scrum Master (PSM)',
      'PSPO': 'Professional Scrum Product Owner (PSPO)',
      'PMI_COMBO': 'PMI Combo (PMP + CAPM)',
      'SCRUM_COMBO': 'Scrum Combo (PSM + PSPO)',
      'ALL_ACCESS': 'All Access Bundle'
    };
    return names[track] || track;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 dark:from-slate-950 dark:to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  const customerName = orderDetails?.customer_name || user?.name || 'there';
  const firstName = customerName.split(' ')[0];
  const customerEmail = orderDetails?.customer_email || user?.email || '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 dark:from-slate-950 dark:to-slate-900 p-4 md:p-8">
      <div className="max-w-2xl mx-auto">
        
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500 rounded-full mb-6 shadow-lg">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-white mb-2">
            Thank you for your order, {firstName}!
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            Your certification journey starts now
          </p>
        </div>

        {/* Confirmation Card */}
        <Card className="bg-white dark:bg-slate-900 shadow-xl mb-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-4 p-4 bg-green-50 dark:bg-green-950/30 rounded-lg mb-6">
              <Mail className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
              <div>
                <p className="font-medium text-green-800 dark:text-green-300">
                  We are pleased to confirm your order
                  {orderDetails?.order_number && (
                    <span className="font-bold"> #{orderDetails.order_number}</span>
                  )}
                </p>
                <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                  A confirmation email has been sent to <strong>{customerEmail}</strong>
                </p>
              </div>
            </div>

            {/* CTA Button */}
            <Button 
              onClick={() => navigate('/dashboard')}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold mb-6"
              data-testid="start-course-btn"
            >
              <BookOpen className="w-5 h-5 mr-2" />
              Start Your Course
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>

            {/* Order Details */}
            <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
              <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2 mb-4">
                <Receipt className="w-5 h-5 text-slate-500" />
                Order Details
              </h3>
              
              <div className="space-y-3">
                {/* Purchased Items */}
                <div className="flex justify-between items-start">
                  <span className="text-slate-600 dark:text-slate-400">Product</span>
                  <div className="text-right">
                    {orderDetails?.tracks ? (
                      orderDetails.tracks.map((track, idx) => (
                        <Badge key={idx} variant="outline" className="ml-2 mb-1">
                          {getTrackFullName(track)}
                        </Badge>
                      ))
                    ) : orderDetails?.track ? (
                      <Badge variant="outline">{getTrackFullName(orderDetails.track)}</Badge>
                    ) : (
                      <Badge variant="outline">Course Access</Badge>
                    )}
                  </div>
                </div>

                {/* Access Duration */}
                {orderDetails?.validity_months && (
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">Access Duration</span>
                    <span className="font-medium">{orderDetails.validity_months} months</span>
                  </div>
                )}

                {/* Original Price */}
                {orderDetails?.original_amount && (
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">Subtotal</span>
                    <span className="font-medium">€{(orderDetails.original_amount / 100).toFixed(2)}</span>
                  </div>
                )}

                {/* Coupon */}
                {orderDetails?.coupon_code && (
                  <div className="flex justify-between items-center bg-amber-50 dark:bg-amber-950/30 -mx-2 px-2 py-2 rounded">
                    <span className="text-amber-700 dark:text-amber-400 flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      Coupon Applied
                    </span>
                    <div className="text-right">
                      <code className="bg-amber-100 dark:bg-amber-900 px-2 py-1 rounded text-sm font-mono">
                        {orderDetails.coupon_code}
                      </code>
                      {orderDetails.discount_percent && (
                        <span className="ml-2 text-green-600 font-medium">
                          -{orderDetails.discount_percent}%
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* VAT */}
                {orderDetails?.tax_amount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">VAT ({orderDetails.tax_rate || 0}%)</span>
                    <span className="font-medium">€{(orderDetails.tax_amount / 100).toFixed(2)}</span>
                  </div>
                )}

                {/* Total */}
                <div className="flex justify-between border-t border-slate-200 dark:border-slate-700 pt-3 mt-3">
                  <span className="font-semibold text-slate-800 dark:text-white">Total Paid</span>
                  <span className="font-bold text-lg text-green-600">
                    €{orderDetails?.amount_paid ? (orderDetails.amount_paid / 100).toFixed(2) : '0.00'}
                  </span>
                </div>
              </div>
            </div>

            {/* Customer Details */}
            <div className="border-t border-slate-200 dark:border-slate-700 pt-6 mt-6">
              <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2 mb-4">
                <User className="w-5 h-5 text-slate-500" />
                Customer Details
              </h3>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Name</span>
                  <span className="font-medium">{customerName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Email</span>
                  <span className="font-medium">{customerEmail}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Additional Info */}
        <div className="text-center text-sm text-slate-500 dark:text-slate-400">
          <p>A detailed invoice with VAT breakdown has been sent to your email by Stripe.</p>
          <p className="mt-2">
            Questions? Contact us at{' '}
            <a href="mailto:support@exam-trainer.com" className="text-green-600 hover:underline">
              support@exam-trainer.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
